export const KNOWN_RUN_SUBJECT_TYPES = ['conversation', 'onboarding', 'delegation', 'work_item'];
export const RunSubjectType = {
    Conversation: 'conversation',
    Delegation: 'delegation',
    Onboarding: 'onboarding',
    WorkItem: 'work_item',
};
export const KNOWN_AGENT_RUN_STATUSES = [
    'queued',
    'running',
    'awaiting_request',
    'recovering',
    'paused',
    /**
     * TODO(backcompat): read-only legacy member (`agent-run-continuity:delete-awaiting-reconciliation-status`). No code path may
     * enter this status; it is retained so historical rows and replayed
     * `run.recovery.reconciliation_required.v1` events still decode and render.
     * It is deliberately absent from `KNOWN_AGENT_RUN_ACTIVE_STATUSES`.
     * Removal condition: the last such event ages out of timeline retention.
     */
    'awaiting_reconciliation',
    'completed',
    'failed',
    'interrupted',
    'cancelled',
];
export const AgentRunStatus = {
    /**
     * TODO(backcompat): read-only legacy value (`agent-run-continuity:delete-awaiting-reconciliation-status`). Published SDK
     * surface, so it stays here for consumers rendering historical runs; the
     * agent-runs never writes it. Removal condition: the last such event ages
     * out of timeline retention.
     */
    AwaitingReconciliation: 'awaiting_reconciliation',
    /** Waiting for an outstanding request; Needs input only when it awaits a person. Not a user pause. */
    AwaitingRequest: 'awaiting_request',
    /** Read-only historical terminal status, displayed as Stopped; no live writer. */
    Cancelled: 'cancelled',
    /** Terminal success, displayed as Done. */
    Completed: 'completed',
    /** Terminal error, displayed as Failed with its reason. */
    Failed: 'failed',
    /** Terminal early stop, displayed as Stopped. A new message starts a new Run. */
    Interrupted: 'interrupted',
    /** Admitted and waiting for a worker, displayed as Starting; not a queued message. */
    Queued: 'queued',
    /** A worker is restoring execution, displayed as Working. */
    Recovering: 'recovering',
    /** Executing the current turn, displayed as Working. */
    Running: 'running',
    /** User-held, nonterminal Run; retains its Conversation and pending question. Step 5b enables writes. */
    Paused: 'paused',
};
/**
 * `AgentRunStatus.AwaitingReconciliation` is deliberately NOT a member
 * (`agent-run-continuity:delete-awaiting-reconciliation-status`): the parked state is deleted as a state the system can enter,
 * so no run is ever active in it. It remains decodable in
 * `KNOWN_AGENT_RUN_STATUSES` for historical rows only.
 */
export const KNOWN_AGENT_RUN_ACTIVE_STATUSES = [
    AgentRunStatus.Queued,
    AgentRunStatus.Running,
    AgentRunStatus.AwaitingRequest,
    AgentRunStatus.Recovering,
    AgentRunStatus.Paused,
];
export const KNOWN_AGENT_RUN_TERMINAL_STATUSES = [
    AgentRunStatus.Completed,
    AgentRunStatus.Failed,
    AgentRunStatus.Interrupted,
    AgentRunStatus.Cancelled,
];
/**
 * Which party produced the error that failed a Run, carried as the optional `failure_origin` field of a `run.failed.v1`
 * payload. `model_provider`: the model provider behind the Run's route returned the error (a Codex subscription limit or
 * revoked sign-in, a Claude subscription limit or refused sign-in, a provider API error), and `provider_error.provider_id`
 * names that provider. An absent field means Crewstack, the desktop host, or an unclassified cause; it never means the
 * provider.
 *
 * Does NOT say whether sending again can succeed, and never names a credential. Extend only for a party whose errors a
 * client presents differently from both Crewstack's and the provider's.
 */
export const RUN_FAILURE_ORIGINS = ['model_provider'];
export const RunFailureOrigin = {
    ModelProvider: 'model_provider',
};
export function isRunFailureOrigin(value) {
    return RUN_FAILURE_ORIGINS.some((origin) => origin === value);
}
export const KNOWN_AGENT_RUN_TRANSCRIPT_STATUSES = [
    AgentRunStatus.Queued,
    AgentRunStatus.Running,
    AgentRunStatus.AwaitingRequest,
    AgentRunStatus.Paused,
    AgentRunStatus.Completed,
    AgentRunStatus.Failed,
    AgentRunStatus.Interrupted,
    AgentRunStatus.Cancelled,
];
export const KNOWN_AGENT_RUN_RECOVERY_STATES = [
    'active',
    'waiting',
    'paused',
    'recovering',
    'reconciliation_required',
    'settled',
];
export const AgentRunRecoveryState = {
    Active: 'active',
    Waiting: 'waiting',
    /** TODO(backcompat): remove after `run-interruption-control` PRD Step 5b backfill and zero legacy rows/replay. */
    Paused: 'paused',
    Recovering: 'recovering',
    ReconciliationRequired: 'reconciliation_required',
    Settled: 'settled',
};
export const RUN_INPUT_FAILURE_KINDS = [
    'model_input_capability_drift',
    'model_input_source_unavailable',
    'model_input_resource_budget_exceeded',
    'model_input_snapshot_unavailable',
];
export const RunInputFailureKind = {
    CapabilityDrift: 'model_input_capability_drift',
    SourceUnavailable: 'model_input_source_unavailable',
    ResourceBudgetExceeded: 'model_input_resource_budget_exceeded',
    SnapshotUnavailable: 'model_input_snapshot_unavailable',
};
export const MODEL_INPUT_SNAPSHOT_UNAVAILABLE_REASONS = [
    'lost',
    'corrupt',
    'expired',
    'authorization_revoked',
];
export const RUN_INPUT_SOURCE_UNAVAILABLE_REASONS = [
    'not_found_or_denied',
    'lease_expired',
    'version_mismatch',
    'purged',
];
export const RUN_INPUT_RESOURCE_BUDGETS = [
    'worker_resident_bytes',
    'provider_preparation_concurrency',
    'knowledge_range_concurrency',
    'attachment_metadata_bytes',
];
export const KNOWN_AGENT_RUN_REQUEST_STATUSES = ['pending', 'resolved'];
export const AgentRunRequestStatus = {
    Pending: 'pending',
    Resolved: 'resolved',
};
export const KNOWN_AGENT_RUN_CLIENT_REQUEST_STATUSES = ['pending', 'completed', 'failed'];
export const AgentRunClientRequestStatus = {
    Completed: 'completed',
    Failed: 'failed',
    Pending: 'pending',
};
export const KNOWN_AGENT_RUN_OPERATION_KINDS = ['tool_call'];
export const AgentRunOperationKind = {
    ToolCall: 'tool_call',
};
export const KNOWN_AGENT_RUN_OPERATION_STATUSES = [
    'started',
    'streaming',
    'succeeded',
    'failed',
    'awaiting_request',
    'unknown_after_restart',
];
export const AgentRunOperationStatus = {
    AwaitingRequest: 'awaiting_request',
    Failed: 'failed',
    Started: 'started',
    Streaming: 'streaming',
    Succeeded: 'succeeded',
    UnknownAfterRestart: 'unknown_after_restart',
};
export const KNOWN_AGENT_RUN_TOOL_CALL_STATUSES = ['running', 'completed', 'failed'];
export const AgentRunToolCallStatus = {
    Completed: 'completed',
    Failed: 'failed',
    Running: 'running',
};
export const KNOWN_AGENT_RUN_TOOL_CALL_RESOLUTIONS = ['resolved', 'denied'];
export const AgentRunToolCallResolution = {
    Denied: 'denied',
    Resolved: 'resolved',
};
/**
 * Request choice ids that refuse what was asked: answering with one resolves a tool call as
 * `denied`, and a chat card's cancel sends one when the request offers it
 * (`chat-cards-inline-questions:card-cancel-effects`). It is not every "no" a request can offer, only the
 * refusals the Runtime reads as denial.
 */
export const KNOWN_AGENT_RUN_DENIAL_CHOICE_IDS = ['deny', 'reject'];
export const KNOWN_AGENT_RUN_STORED_TOOL_BATCH_STATUSES = [
    'in_progress',
    'ready_to_submit',
    'awaiting_request',
];
export const AgentRunStoredToolBatchStatus = {
    AwaitingRequest: 'awaiting_request',
    InProgress: 'in_progress',
    ReadyToSubmit: 'ready_to_submit',
};
export const KNOWN_AGENT_RUN_REQUEST_TYPES = [
    'approval',
    'ask_user',
    'budget_exceeded',
    'credential_entry',
    'external_effect_resolution',
    // TODO(backcompat): `project_name`, `repository_connection` and `repository_selection` were
    // the repository-first coach's requests, retired by initiative `goal-first-onboarding` phase 9. No producer emits
    // them; they stay so run requests and timeline events persisted before the retirement still
    // decode. Delete them once production holds no run request row or timeline event of these types.
    'project_name',
    'repository_connection',
    'repository_selection',
    'onboarding_context',
    'onboarding_discipline',
    'onboarding_goal_selection',
    'tool_result_batch',
    'work_item_clarification',
    'work_item_decision',
];
export const AgentRunRequestType = {
    Approval: 'approval',
    AskUser: 'ask_user',
    BudgetExceeded: 'budget_exceeded',
    CredentialEntry: 'credential_entry',
    ExternalEffectResolution: 'external_effect_resolution',
    /** TODO(backcompat): legacy repository-first request (`goal-first-onboarding` phase 9); see KNOWN_AGENT_RUN_REQUEST_TYPES. */
    ProjectName: 'project_name',
    RepositoryConnection: 'repository_connection',
    RepositorySelection: 'repository_selection',
    OnboardingContext: 'onboarding_context',
    OnboardingDiscipline: 'onboarding_discipline',
    OnboardingGoalSelection: 'onboarding_goal_selection',
    ToolResultBatch: 'tool_result_batch',
    WorkItemClarification: 'work_item_clarification',
    WorkItemDecision: 'work_item_decision',
};
export const KNOWN_AGENT_RUN_EVENT_TYPES = [
    'run.created.v1',
    'run.started.v1',
    'run.completed.v1',
    'run.failed.v1',
    'run.interrupted.v1',
    'run.paused.v1',
    'run.resumed.v1',
    'run.cancelled.v1',
    'run.recovery.started.v1',
    'run.recovery.resumed.v1',
    'run.recovery.paused.v1',
    'run.recovery.reconciliation_required.v1',
    'run.status.updated.v1',
    'run.context.assessed.v1',
    'context.truncated.v1',
    'run.turn.usage.v1',
    'run.turn.retried.v1',
    'model.invocation.prepared.v1',
    'conversation.message.accepted.v1',
    'conversation.message.delta.v1',
    'conversation.message.committed.v1',
    'conversation.reasoning.delta.v1',
    'request.created.v1',
    'request.resolved.v1',
    'tool.call.started.v1',
    'tool.call.output.appended.v1',
    'tool.call.completed.v1',
    'tool.call.failed.v1',
    'tool.call.blocked.v1',
    'tool.call.unblocked.v1',
    'tool.call.warning.v1',
];
export const AgentRunEventType = {
    ConversationMessageAccepted: 'conversation.message.accepted.v1',
    ConversationMessageCommitted: 'conversation.message.committed.v1',
    ConversationMessageDelta: 'conversation.message.delta.v1',
    ConversationReasoningDelta: 'conversation.reasoning.delta.v1',
    ContextTruncated: 'context.truncated.v1',
    ModelInvocationPrepared: 'model.invocation.prepared.v1',
    RequestCreated: 'request.created.v1',
    RequestResolved: 'request.resolved.v1',
    RunCancelled: 'run.cancelled.v1',
    RunCompleted: 'run.completed.v1',
    RunContextAssessed: 'run.context.assessed.v1',
    RunCreated: 'run.created.v1',
    RunFailed: 'run.failed.v1',
    RunInterrupted: 'run.interrupted.v1',
    RunPaused: 'run.paused.v1',
    RunResumed: 'run.resumed.v1',
    /** Historical recovery wait event; not a user pause. Preserve its wire name for replay. */
    RunRecoveryPaused: 'run.recovery.paused.v1',
    RunRecoveryReconciliationRequired: 'run.recovery.reconciliation_required.v1',
    RunRecoveryResumed: 'run.recovery.resumed.v1',
    RunRecoveryStarted: 'run.recovery.started.v1',
    RunStarted: 'run.started.v1',
    RunStatusUpdated: 'run.status.updated.v1',
    RunTurnRetried: 'run.turn.retried.v1',
    RunTurnUsage: 'run.turn.usage.v1',
    ToolCallBlocked: 'tool.call.blocked.v1',
    ToolCallCompleted: 'tool.call.completed.v1',
    ToolCallFailed: 'tool.call.failed.v1',
    ToolCallOutputAppended: 'tool.call.output.appended.v1',
    ToolCallStarted: 'tool.call.started.v1',
    ToolCallUnblocked: 'tool.call.unblocked.v1',
    ToolCallWarning: 'tool.call.warning.v1',
};
export const KNOWN_AGENT_RUN_TERMINAL_EVENT_TYPES = [
    AgentRunEventType.RunCompleted,
    AgentRunEventType.RunFailed,
    AgentRunEventType.RunInterrupted,
    AgentRunEventType.RunCancelled,
];
export const KNOWN_AGENT_RUN_STRUCTURAL_EVENT_TYPES = [
    AgentRunEventType.RunStarted,
    AgentRunEventType.RunPaused,
    AgentRunEventType.RunResumed,
    AgentRunEventType.ConversationMessageCommitted,
    AgentRunEventType.ToolCallStarted,
    AgentRunEventType.ToolCallCompleted,
    AgentRunEventType.ToolCallFailed,
    AgentRunEventType.ToolCallUnblocked,
    AgentRunEventType.RequestCreated,
    AgentRunEventType.RequestResolved,
    ...KNOWN_AGENT_RUN_TERMINAL_EVENT_TYPES,
];
export const KNOWN_AGENT_RUN_TOOL_CALL_EVENT_TYPES = [
    AgentRunEventType.ToolCallStarted,
    AgentRunEventType.ToolCallOutputAppended,
    AgentRunEventType.ToolCallCompleted,
    AgentRunEventType.ToolCallFailed,
    AgentRunEventType.ToolCallBlocked,
    AgentRunEventType.ToolCallUnblocked,
    AgentRunEventType.ToolCallWarning,
];
export const KNOWN_AGENT_RUN_OPERATIONAL_TOOL_CALL_EVENT_TYPES = [
    AgentRunEventType.ToolCallStarted,
    AgentRunEventType.ToolCallOutputAppended,
    AgentRunEventType.ToolCallCompleted,
    AgentRunEventType.ToolCallFailed,
    AgentRunEventType.ToolCallBlocked,
    AgentRunEventType.ToolCallUnblocked,
];
export const KNOWN_AGENT_RUN_AGGREGATE_STATUSES = ['active', 'failed', 'completed', 'cancelled', 'idle'];
export const AgentRunAggregateStatus = {
    Active: 'active',
    Cancelled: 'cancelled',
    Completed: 'completed',
    Failed: 'failed',
    Idle: 'idle',
};
export const KNOWN_AGENT_RUN_WORK_ITEM_ANCHOR_REGISTERED_BY_KINDS = [
    'tool_dispatcher',
    'orchestrator_tool',
    'system',
];
export const AgentRunWorkItemAnchorRegisteredByKind = {
    OrchestratorTool: 'orchestrator_tool',
    System: 'system',
    ToolDispatcher: 'tool_dispatcher',
};
/** A delegated child as its parent sees it: running while its latest run is active, finished otherwise. */
export const KNOWN_AGENT_RUN_DELEGATION_STATUSES = ['running', 'finished'];
export const AgentRunDelegationStatus = {
    Finished: 'finished',
    Running: 'running',
};
/** How a finished child's latest run ended. */
export const KNOWN_AGENT_RUN_DELEGATION_OUTCOMES = ['completed', 'failed', 'cancelled'];
export const AgentRunDelegationOutcome = {
    Cancelled: 'cancelled',
    Completed: 'completed',
    Failed: 'failed',
};
/**
 * How an assignment opened a chat (`sub-sessions:sub-session-is-an-assignment-kind`), read on a Conversation as
 * `openedAs` and on a delegation-tree node as `assignmentKind`. `delegation` is work handed to an agent, a chat that
 * chat lists drop; `sub_session` is a chat the agent opened for the person, listed one level under its parent and its
 * own delegation root. It says how the chat was opened, never who started a turn there or who can read it. Mirrors
 * `AssignmentKind` in `@crewstack/domain/agent-runs/vocabulary`; a kind newer than this package decodes as its own
 * string, which no reader takes for either kind.
 */
export const KNOWN_ASSIGNMENT_KINDS = ['delegation', 'sub_session'];
export const AssignmentKind = {
    Delegation: 'delegation',
    SubSession: 'sub_session',
};
/** The two delegation events: a child run started, a child run finished. */
export const KNOWN_AGENT_RUN_DELEGATION_EVENT_TYPES = ['delegation.started.v1', 'delegation.finished.v1'];
export const AgentRunDelegationEventType = {
    Finished: 'delegation.finished.v1',
    Started: 'delegation.started.v1',
};
export const AGENT_RUN_STATUSES = KNOWN_AGENT_RUN_STATUSES;
export const AGENT_RUN_ACTIVE_STATUSES = KNOWN_AGENT_RUN_ACTIVE_STATUSES;
export const AGENT_RUN_TERMINAL_STATUSES = KNOWN_AGENT_RUN_TERMINAL_STATUSES;
export const AGENT_RUN_TRANSCRIPT_STATUSES = KNOWN_AGENT_RUN_TRANSCRIPT_STATUSES;
export const AGENT_RUN_RECOVERY_STATES = KNOWN_AGENT_RUN_RECOVERY_STATES;
export const AGENT_RUN_REQUEST_STATUSES = KNOWN_AGENT_RUN_REQUEST_STATUSES;
export const AGENT_RUN_CLIENT_REQUEST_STATUSES = KNOWN_AGENT_RUN_CLIENT_REQUEST_STATUSES;
export const AGENT_RUN_OPERATION_KINDS = KNOWN_AGENT_RUN_OPERATION_KINDS;
export const AGENT_RUN_OPERATION_STATUSES = KNOWN_AGENT_RUN_OPERATION_STATUSES;
export const AGENT_RUN_TOOL_CALL_STATUSES = KNOWN_AGENT_RUN_TOOL_CALL_STATUSES;
export const AGENT_RUN_TOOL_CALL_RESOLUTIONS = KNOWN_AGENT_RUN_TOOL_CALL_RESOLUTIONS;
export const AGENT_RUN_STORED_TOOL_BATCH_STATUSES = KNOWN_AGENT_RUN_STORED_TOOL_BATCH_STATUSES;
export const AGENT_RUN_REQUEST_TYPES = KNOWN_AGENT_RUN_REQUEST_TYPES;
export const AGENT_RUN_EVENT_TYPES = KNOWN_AGENT_RUN_EVENT_TYPES;
export const AGENT_RUN_TERMINAL_EVENT_TYPES = KNOWN_AGENT_RUN_TERMINAL_EVENT_TYPES;
export const AGENT_RUN_STRUCTURAL_EVENT_TYPES = KNOWN_AGENT_RUN_STRUCTURAL_EVENT_TYPES;
export const AGENT_RUN_TOOL_CALL_EVENT_TYPES = KNOWN_AGENT_RUN_TOOL_CALL_EVENT_TYPES;
export const AGENT_RUN_OPERATIONAL_TOOL_CALL_EVENT_TYPES = KNOWN_AGENT_RUN_OPERATIONAL_TOOL_CALL_EVENT_TYPES;
export const AGENT_RUN_AGGREGATE_STATUSES = KNOWN_AGENT_RUN_AGGREGATE_STATUSES;
export const AGENT_RUN_WORK_ITEM_ANCHOR_REGISTERED_BY_KINDS = KNOWN_AGENT_RUN_WORK_ITEM_ANCHOR_REGISTERED_BY_KINDS;
export const AGENT_RUN_DELEGATION_STATUSES = KNOWN_AGENT_RUN_DELEGATION_STATUSES;
export const AGENT_RUN_DELEGATION_OUTCOMES = KNOWN_AGENT_RUN_DELEGATION_OUTCOMES;
export const AGENT_RUN_DELEGATION_EVENT_TYPES = KNOWN_AGENT_RUN_DELEGATION_EVENT_TYPES;
export const RUN_SUBJECT_TYPES = KNOWN_RUN_SUBJECT_TYPES;
export function isKnownRunSubjectType(value) {
    return KNOWN_RUN_SUBJECT_TYPES.includes(value);
}
export function isKnownAgentRunStatus(value) {
    return KNOWN_AGENT_RUN_STATUSES.includes(value);
}
export function isKnownAgentRunTranscriptStatus(value) {
    return KNOWN_AGENT_RUN_TRANSCRIPT_STATUSES.includes(value);
}
export function isKnownAgentRunClientRequestStatus(value) {
    return KNOWN_AGENT_RUN_CLIENT_REQUEST_STATUSES.includes(value);
}
export function isKnownAgentRunRequestType(value) {
    return KNOWN_AGENT_RUN_REQUEST_TYPES.includes(value);
}
export function isKnownAgentRunToolCallStatus(value) {
    return KNOWN_AGENT_RUN_TOOL_CALL_STATUSES.includes(value);
}
export function isKnownAgentRunToolCallResolution(value) {
    return KNOWN_AGENT_RUN_TOOL_CALL_RESOLUTIONS.includes(value);
}
export function isKnownAgentRunEventType(value) {
    return KNOWN_AGENT_RUN_EVENT_TYPES.includes(value);
}
export function isKnownAgentRunAggregateStatus(value) {
    return KNOWN_AGENT_RUN_AGGREGATE_STATUSES.includes(value);
}
export function isKnownAgentRunDelegationStatus(value) {
    return KNOWN_AGENT_RUN_DELEGATION_STATUSES.includes(value);
}
export function isKnownAgentRunDelegationOutcome(value) {
    return KNOWN_AGENT_RUN_DELEGATION_OUTCOMES.includes(value);
}
/**
 * How agent-runs reduced a Run's context before an invocation (`context-intelligence:oversized-bodies-seekable-by-handle`, `context-intelligence:honest-last-resort-reduction`).
 *
 * Owned here rather than in agent-runs because the value crosses a wire: the runtime
 * mints it, `run.context.assessed.v1` and the platform API carry it, and the chat badge
 * renders it. It lives in the same package as every other agent-run wire vocabulary so a
 * producer rename fails at typecheck on BOTH sides instead of silently falling into the
 * renderer's default branch and reporting loss where there is none.
 */
export const KNOWN_AGENT_RUN_CONTEXT_REDUCTION_STRATEGIES = [
    /** Nothing was changed; the assembled context already fit. */
    'unchanged',
    /** Portable Core compaction produced the context. */
    'portable',
    /**
     * Oversized bodies were replaced by head + tail + a resolvable handle. Nothing was
     * lost: the agent reads the rest through `system.read_context_body`.
     */
    'seekable_bodies',
    /** Last-resort deterministic reduction; earlier messages WERE dropped. */
    'deterministic',
];
export const AgentRunContextReductionStrategy = {
    Deterministic: 'deterministic',
    Portable: 'portable',
    SeekableBodies: 'seekable_bodies',
    Unchanged: 'unchanged',
};
//# sourceMappingURL=vocabulary.js.map